import axios from 'axios';
import { Modal } from 'antd';
import { API_CONFIG, LOG_CONFIG } from '../config/api';

// 判断是否为「个人中心邮箱未验证」导致的拒绝
export const isProfileVerifyRequired = (error: any): boolean => {
  if (error?.isProfileVerifyRequired) return true;
  if (error?.response?.status !== 403) return false;
  const detail = error?.response?.data?.detail;
  return typeof detail === 'string' && detail.includes('邮箱验证');
};

// 全局只弹一次引导，避免并发请求重复弹窗
let verifyGuideVisible = false;

// 用户点「稍后再说」后，本次会话内不再重复弹窗（sessionStorage 随标签页关闭自动清除）
const VERIFY_GUIDE_DISMISSED_KEY = 'profile_verify_guide_dismissed';

const isVerifyGuideDismissed = (): boolean => {
  try {
    return window.sessionStorage.getItem(VERIFY_GUIDE_DISMISSED_KEY) === '1';
  } catch {
    return false;
  }
};

// 验证成功或退出登录后调用，恢复弹窗引导
export const resetProfileVerifyGuide = () => {
  try {
    window.sessionStorage.removeItem(VERIFY_GUIDE_DISMISSED_KEY);
  } catch {
    // 忽略存储异常
  }
};

const showProfileVerifyGuide = () => {
  if (verifyGuideVisible) return;
  // 用户已选择「稍后再说」，本次会话内不再打扰
  if (isVerifyGuideDismissed()) return;
  // 个人中心页面自身有验证卡片，无需再弹引导
  if (window.location.pathname.startsWith('/auth/profile')) return;
  verifyGuideVisible = true;
  Modal.confirm({
    title: '请先到个人中心验证邮箱',
    content: '为保护你的任务数据，查看历史任务前需要验证邮箱归属。验证码会发送到你的登录邮箱，验证一次即可。',
    okText: '去验证邮箱',
    cancelText: '稍后再说',
    onOk: () => {
      window.location.href = '/auth/profile';
    },
    onCancel: () => {
      // 记住用户的选择，本次会话内不再弹出
      try {
        window.sessionStorage.setItem(VERIFY_GUIDE_DISMISSED_KEY, '1');
      } catch {
        // 忽略存储异常
      }
    },
    afterClose: () => {
      verifyGuideVisible = false;
    },
  });
};

// 任务管理API客户端
const taskManagerApi = axios.create({
  baseURL: API_CONFIG.TASK_MANAGER_BASE,
  timeout: API_CONFIG.TIMEOUT.LONG, // 20分钟超时时间
  withCredentials: true, // 自动携带cookies
  headers: {
    'Content-Type': 'application/json',
  },
});

// 请求拦截器（仅在开启日志时输出）
taskManagerApi.interceptors.request.use(
  (config) => {
    if (LOG_CONFIG.ENABLED) {
      console.log('任务管理API请求:', config.method, config.url);
    }
    return config;
  },
  (error) => {
    if (LOG_CONFIG.ENABLED) {
      console.error('任务管理API请求错误:', error);
    }
    return Promise.reject(error);
  }
);

// 响应拦截器：总是返回 response.data，日志按配置输出
taskManagerApi.interceptors.response.use(
  (response) => {
    if (LOG_CONFIG.ENABLED) {
      console.log('任务管理API响应:', response.status, response.data);
    }
    return response.data;
  },
  (error) => {
    if (LOG_CONFIG.ENABLED) {
      console.error('任务管理API响应错误:', error.response?.data || error.message);
    }
    // 如果是401错误，可以在这里处理登录过期
    if (error.response?.status === 401) {
      console.warn('用户未认证，可能需要重新登录');
    }
    // 邮箱未验证：弹出引导而非让各页面报「加载失败」
    if (isProfileVerifyRequired(error)) {
      error.isProfileVerifyRequired = true;
      showProfileVerifyGuide();
    }
    return Promise.reject(error);
  }
);

export default taskManagerApi;